/**
 * "Submit to an MGA / wholesaler" — a two-step modal:
 *  1) confirm the account profile the wholesaler needs (pre-filled from the submission)
 *  2) review/edit an AI-drafted submission email, then send it (the uploaded docs
 *     are attached automatically server-side).
 */
import { AAS_CONTACT, LOBS, LOB_NAME, MIME_EXT, type SubmissionData } from '@commpass/shared';
import { useMemo, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { currencyInput, fmtDate } from '../lib/format';
import { Modal } from './Modal';
import { useToast } from './Toast';

type Patch = (partial: Partial<SubmissionData>, status?: string) => Promise<void>;

const MAX_EXTRA_TOTAL = 18 * 1024 * 1024; // keep manual files well under the 25 MB request-body limit
const MAX_ONE = 15 * 1024 * 1024;

// Chunked base64 (build one binary string, then encode once) so multi-MB binary
// files aren't corrupted or blow the call stack.
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

type ExtraFile = { name: string; mime: string; data: string; size: number };

// Profile fields the MGA requires before they'll quote.
const REQUIRED: [string, string][] = [
  ['physicalAddress', 'Location'],
  ['yearsInBusiness', 'Years in business'],
  ['grossAnnualRevenue', 'Annual revenue'],
  ['quoteNeedBy', 'Effective / need-by'],
  ['descriptionOfOps', 'Description of operations'],
];

export function MgaModal({
  data,
  patch,
  id,
  onClose,
}: {
  data: SubmissionData;
  patch: Patch;
  id: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const s = data as Record<string, unknown>;
  const init = (k: string) => String(s[k] ?? '');

  const [step, setStep] = useState<'profile' | 'review'>('profile');
  const [businessName, setBusinessName] = useState(init('businessName'));
  const [physicalAddress, setPhysicalAddress] = useState(init('physicalAddress'));
  const [lines, setLines] = useState<string[]>(Array.isArray(data.lines) ? (data.lines as string[]) : []);
  const [yearsInBusiness, setYears] = useState(init('yearsInBusiness'));
  const [grossAnnualRevenue, setRevenue] = useState(init('grossAnnualRevenue'));
  const [annualPayroll, setPayroll] = useState(init('annualPayroll'));
  const [quoteNeedBy, setNeedBy] = useState(init('quoteNeedBy'));
  const [targetPricing, setTarget] = useState(init('targetPricing'));
  const [sicNaics, setSic] = useState(init('sicNaics'));
  const [descriptionOfOps, setDesc] = useState(init('descriptionOfOps'));
  const [knownLosses, setLosses] = useState(init('knownLosses'));
  const [mgaName, setMgaName] = useState(init('mgaName'));
  const [mgaEmail, setMgaEmail] = useState('');
  const [mgaNote, setMgaNote] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  // Attachments: the submission's own uploaded docs (any can be excluded) plus
  // files the specialist adds by hand for this email only.
  const attachable = useMemo(() => {
    const docs = Array.isArray(data.documents) ? (data.documents as Record<string, unknown>[]) : [];
    const reqs = Array.isArray(data.docRequests) ? (data.docRequests as Record<string, unknown>[]) : [];
    const seen = new Set<string>();
    const out: { path: string; name: string }[] = [];
    for (const f of [...docs, ...reqs]) {
      const path = String(f.storagePath ?? '');
      if (!path || seen.has(path)) continue;
      seen.add(path);
      out.push({ path, name: String(f.docName ?? f.fileName ?? f.label ?? 'document') });
    }
    return out;
  }, [data]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [extraFiles, setExtraFiles] = useState<ExtraFile[]>([]);

  function toggleDoc(path: string) {
    setExcluded((s) => {
      const n = new Set(s);
      if (n.has(path)) n.delete(path);
      else n.add(path);
      return n;
    });
  }

  async function addFiles(list: FileList | null) {
    if (!list) return;
    let running = extraFiles.reduce((a, f) => a + f.size, 0);
    for (const file of Array.from(list)) {
      const mime = file.type || '';
      if (!MIME_EXT[mime]) {
        toast(`Can't attach ${file.name} — unsupported type`);
        continue;
      }
      if (file.size > MAX_ONE) {
        toast(`${file.name} is over 15 MB`);
        continue;
      }
      if (running + file.size > MAX_EXTRA_TOTAL) {
        toast('Manual attachments would exceed ~18 MB — send fewer/smaller files');
        break;
      }
      try {
        const encoded = await fileToBase64(file);
        setExtraFiles((cur) => [...cur, { name: file.name, mime, data: encoded, size: file.size }]);
        running += file.size;
      } catch {
        toast(`Could not read ${file.name}`);
      }
    }
  }

  function profile() {
    return {
      businessName,
      physicalAddress,
      lines,
      yearsInBusiness,
      grossAnnualRevenue,
      annualPayroll,
      quoteNeedBy,
      targetPricing,
      sicNaics,
      descriptionOfOps,
      knownLosses,
    };
  }

  function fallbackSummary(): string {
    const linesTxt = lines.map((l) => LOB_NAME[l] ?? l).join(', ');
    const bits: string[] = [];
    bits.push(
      `${businessName || 'The applicant'}${physicalAddress ? ` of ${physicalAddress}` : ''}${
        yearsInBusiness ? `, in business ${yearsInBusiness} years` : ''
      }.`,
    );
    if (linesTxt) bits.push(`Coverage requested: ${linesTxt}.`);
    if (grossAnnualRevenue) bits.push(`Annual revenue ${grossAnnualRevenue}${annualPayroll ? `; payroll ${annualPayroll}` : ''}.`);
    if (knownLosses) bits.push(`Loss history: ${knownLosses}.`);
    return bits.join(' ');
  }

  function toggleLine(lid: string) {
    setLines((ls) => (ls.includes(lid) ? ls.filter((x) => x !== lid) : [...ls, lid]));
  }

  async function build() {
    const p = profile() as Record<string, unknown>;
    for (const [k, label] of REQUIRED) {
      if (!String(p[k] ?? '').trim()) {
        toast(`MGA needs: ${label}`);
        return;
      }
    }
    if (!lines.length) return toast('Select at least one line of business');
    if (!mgaName.trim()) return toast('Enter the MGA / wholesaler name');
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(mgaEmail.trim())) return toast('Enter a valid MGA email');

    setBusy(true);
    try {
      await patch({ ...profile(), mgaName } as Partial<SubmissionData>, 'mga_creating');

      let summary = '';
      const facts = [
        `Named insured: ${businessName}`,
        `Location: ${physicalAddress}`,
        `Lines requested: ${lines.map((l) => LOB_NAME[l] ?? l).join(', ')}`,
        `Years in business: ${yearsInBusiness}`,
        `NAICS/SIC: ${sicNaics}`,
        `Annual revenue: ${grossAnnualRevenue}`,
        `Annual payroll: ${annualPayroll}`,
        `Operations: ${descriptionOfOps}`,
        `Loss history: ${knownLosses}`,
        `Effective / need-by: ${quoteNeedBy}`,
        `Target premium: ${targetPricing}`,
      ].join('\n');
      try {
        const r = await api.aiExecSummary(facts);
        summary = (r.summary || '').trim();
      } catch {
        /* fall through to template */
      }
      if (!summary) summary = fallbackSummary();

      const linesTxt = lines.map((l) => LOB_NAME[l] ?? l).join(', ') || 'Commercial';
      const snapshot = (
        [
          ['Named insured', businessName || '—'],
          ['Location', physicalAddress || '—'],
          ['Lines requested', linesTxt],
          ['Years in business', yearsInBusiness || '—'],
          ['Annual revenue', grossAnnualRevenue || '—'],
          ['Annual payroll', annualPayroll || '—'],
          ['Effective / need-by', fmtDate(quoteNeedBy) || '—'],
          ['Target premium', targetPricing || '—'],
        ] as [string, string][]
      )
        .map(([k, v]) => `• ${k}: ${v}`)
        .join('\n');

      // Greet the underwriter by first name (e.g. "Hi Monica,"), not "Hi Monica Lewis team,".
      const greet = mgaName.trim().split(/\s+/)[0] || mgaName.trim();
      const draft =
        `Hi ${greet},\n\n` +
        `Please find a new commercial submission below for your review and quote consideration. Documents are attached.\n\n` +
        `EXECUTIVE SUMMARY\n${summary}\n\n` +
        `DESCRIPTION OF OPERATIONS\n${descriptionOfOps || '(see attached application)'}\n\n` +
        `ACCOUNT SNAPSHOT\n${snapshot}\n\n` +
        (knownLosses ? `LOSS HISTORY\n${knownLosses}\n\n` : '') +
        (mgaNote ? `${mgaNote}\n\n` : '') +
        `Thank you — happy to provide anything further.\n\n` +
        `${AAS_CONTACT.name} — COMM-PASS\n${AAS_CONTACT.email}`;

      setSubject(`New Commercial Submission — ${businessName || id} (${linesTxt})`);
      setBody(draft);
      setStep('review');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not build the email');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    try {
      const includePaths = attachable.filter((f) => !excluded.has(f.path)).map((f) => f.path);
      const r = await api.sendMgaEmail({
        submissionId: id,
        to: mgaEmail.trim(),
        subject,
        body,
        includePaths,
        extraFiles: extraFiles.map(({ name, mime, data }) => ({ name, mime, data })),
      });
      await patch({ mgaName } as Partial<SubmissionData>, 'mga_sent');
      const skip = r.skipped?.length ? ` — ${r.skipped.length} doc(s) too large to attach` : '';
      toast(r.emailed ? `Sent to ${mgaName} · ${r.attached} attachment(s)${skip}` : 'Marked MGA sent (email flow not configured)');
      onClose();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not send');
    } finally {
      setBusy(false);
    }
  }

  const fg = { marginBottom: 0 } as const;

  return (
    <Modal
      wide={step === 'review'}
      title={step === 'profile' ? 'Submit manually' : `Submission email to ${mgaName}`}
      subtitle={
        step === 'profile'
          ? "Submitting directly to an MGA or a standard carrier's underwriter (not quoted in the portal). Confirm the account profile and their details — we'll draft the email for your review."
          : 'Review the draft, choose which documents to attach (and add any extras), then send.'
      }
      onClose={onClose}
      footer={
        step === 'profile' ? (
          <>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={() => void build()}>
              {busy ? 'Building…' : 'Build submission email'}
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => setStep('profile')}>Back</button>
            <button className="btn btn-primary" disabled={busy} onClick={() => void send()}>
              {busy ? 'Sending…' : 'Send to MGA'}
            </button>
          </>
        )
      }
    >
      {step === 'profile' ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="fgroup" style={fg}>
            <label className="flabel">Named insured</label>
            <input className="finput" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Location</label>
            <input className="finput" placeholder="City, ST" value={physicalAddress} onChange={(e) => setPhysicalAddress(e.target.value)} />
          </div>
          <div>
            <label className="flabel">Lines requested</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginTop: 6 }}>
              {LOBS.map((l) => (
                <label key={l.id} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={lines.includes(l.id)} onChange={() => toggleLine(l.id)} />
                  {l.name}
                </label>
              ))}
            </div>
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Years in business</label>
            <input className="finput" value={yearsInBusiness} onChange={(e) => setYears(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="fgroup" style={{ ...fg, flex: 1 }}>
              <label className="flabel">Annual revenue</label>
              <input className="finput" placeholder="$" value={grossAnnualRevenue} onChange={(e) => setRevenue(currencyInput(e.target.value))} />
            </div>
            <div className="fgroup" style={{ ...fg, flex: 1 }}>
              <label className="flabel">Annual payroll</label>
              <input className="finput" placeholder="$" value={annualPayroll} onChange={(e) => setPayroll(currencyInput(e.target.value))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="fgroup" style={{ ...fg, flex: 1 }}>
              <label className="flabel">Effective / need-by</label>
              <input type="date" className="finput" value={quoteNeedBy} onChange={(e) => setNeedBy(e.target.value)} />
            </div>
            <div className="fgroup" style={{ ...fg, flex: 1 }}>
              <label className="flabel">Target premium</label>
              <input className="finput" placeholder="$" value={targetPricing} onChange={(e) => setTarget(currencyInput(e.target.value))} />
            </div>
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">SIC / NAICS</label>
            <input className="finput" value={sicNaics} onChange={(e) => setSic(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Description of operations</label>
            <textarea className="finput" rows={2} value={descriptionOfOps} onChange={(e) => setDesc(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Loss history</label>
            <textarea className="finput" rows={2} placeholder="Summarize any losses in the last 5 years." value={knownLosses} onChange={(e) => setLosses(e.target.value)} />
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 0', paddingTop: 10, fontWeight: 700, color: 'var(--orange)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Market / underwriter
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Market / underwriter name</label>
            <input className="finput" placeholder="e.g. RPS, Burns & Wilcox, Travelers" value={mgaName} onChange={(e) => setMgaName(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Submissions email</label>
            <input className="finput" placeholder="submissions@market.com" value={mgaEmail} onChange={(e) => setMgaEmail(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Anything specific for them? (optional)</label>
            <textarea className="finput" rows={2} placeholder="e.g. Targeting GL + property. Need terms by 7/15." value={mgaNote} onChange={(e) => setMgaNote(e.target.value)} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="sub-meta">
            <b>To:</b> {mgaEmail}
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Subject</label>
            <input className="finput" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="fgroup" style={fg}>
            <label className="flabel">Email body (editable)</label>
            <textarea className="finput" rows={12} style={{ fontSize: 12.5, lineHeight: 1.5 }} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>

          <div className="fgroup" style={fg}>
            <label className="flabel">Attachments</label>
            <div className="sub-meta" style={{ marginBottom: 8 }}>
              {attachable.length > 0 ? 'Uncheck any you don’t want to send. ' : 'No documents on this submission yet. '}
              Add extra files below (this email only — they’re not saved to the submission).
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {attachable.map((f) => (
                <label key={f.path} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={!excluded.has(f.path)} onChange={() => toggleDoc(f.path)} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                </label>
              ))}
              {extraFiles.map((f, i) => (
                <div key={`x${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <span style={{ color: 'var(--orange-deep)', fontWeight: 700 }}>+</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <button className="reset-link" onClick={() => setExtraFiles((cur) => cur.filter((_, j) => j !== i))}>Remove</button>
                </div>
              ))}
            </div>
            <label className="up-btn" style={{ marginTop: 10, display: 'inline-flex', cursor: 'pointer' }}>
              + Attach a file
              <input
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { void addFiles(e.target.files); e.currentTarget.value = ''; }}
              />
            </label>
          </div>
        </div>
      )}
    </Modal>
  );
}
